// Hook to fetch the /tenantProvider response (providerConfig)

/*  Shared  */
import { Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  React  */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const useFetchTenantProvider = (clientId) => {
    const [providerConfig, setProviderConfig] = useState(null);
    const [loading, setLoading] = useState(true);

    const params = useMemo(
        () =>
            clientId
                ? { includeClients: true, platformTenantId: clientId }
                : {},
        [clientId]
    );

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get('/tenantProvider', {
                    cancelToken: source.token,
                    params
                });
                if (!Response.hasData(response)) {
                    debug.e('No Tenant Provider Config found', response);
                }
                setProviderConfig(Response.getOne(response));
            } catch (error) {
                debug.e('Unable to get Tenant Provider Config', error);
            }
            setLoading(false);
        };

        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [params]);

    return {
        loading,
        providerConfig
    };
};

// Hook to fetch the /tenantProvider data async
export const useFetchTenantProviderAsync = () => {
    const error = useRef(null);
    const providerConfig = useRef(null);

    const getProviderConfig = useCallback(async (clientId) => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const params = { includeClients: true, platformTenantId: clientId };

        try {
            const response = await axios.get('/tenantProvider', {
                cancelToken: source.token,
                params
            });
            if (!Response.hasData(response)) {
                debug.e('No Tenant Provider Config found', response);
            } else {
                providerConfig.current = Response.getOne(response);
            }
        } catch (err) {
            error.current = err;
            debug.e('Error getting provider config information.', err);
        }
        return {
            error: error.current,
            providerConfig: providerConfig.current
        };
    }, []);
    return { getProviderConfig };
};

export const useCreateTenantProviderAsync = () => {
    const [saving, setSaving] = useState(false);

    const createTenantProvider = useCallback(async (providerConfig) => {
        let error = null;
        let success = false;
        try {
            setSaving(true);
            // Upsert by default
            await axios.post('/tenantProvider', providerConfig);
            success = true;
        } catch (err) {
            error = err;
            debug.e('Error creating tenant provider.', err);
        }
        setSaving(false);
        return {
            error,
            success
        };
    }, []);

    return { createTenantProvider, saving };
};
